import { useState, KeyboardEvent } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Divider, Position, Spinner } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import { Button, ButtonCopy, SimplifiedTooltip, TextField, styled } from '@moleculeone/ui';

import { updateRequestName } from '@f/services/batch-search/batch-search.actions';

interface BatchSearchRequestNameProps {
  batch: {
    id: string;
    name: string;
    shareInfo: { sharedWithMe: boolean };
    isHovered?: boolean;
  };
  linked?: boolean;
  copyable?: boolean;
  showButton?: boolean;
}

const Flex = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
});

const SideContainer = styled('div', {
  width: '$7',
  height: '$8',
  display: 'flex',
  alignItems: 'center',
});

const StyledNavLink = styled(NavLink, {
  'color': '#1c2127',
  'textDecoration': 'none',
  'borderBottom': '1px dotted var(--colors-slate9)',
  '&:hover, &:focus-visible': {
    textDecoration: 'none',
    color: 'var(--colors-purple9)',
    borderBottomColor: 'var(--colors-purple9)',
  },
});

const CopyButtonsContainer = styled('div', {
  display: 'flex',
  padding: '$2',
});

const ButtonContainer = styled('div', {
  width: '$10',
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'center',
});

const StyledCopyButton = ({ copy, text }) => {
  return (
    <ButtonContainer>
      <ButtonCopy copy={copy} text={text} weight="minimal" size="small" css={{ width: '100%' }} />
    </ButtonContainer>
  );
};

export function BatchSearchRequestName({
  batch,
  linked = false,
  copyable = false,
  showButton,
}: BatchSearchRequestNameProps) {
  const dispatch = useDispatch();

  const { name: batchName, id, shareInfo } = batch;

  const [editable, setEditable] = useState(false);
  const [name, setName] = useState(batchName);
  const [isSaving, setIsSaving] = useState(false);
  const [isPopupOpen, setPopupOpen] = useState(false);

  const saveName = () => {
    setIsSaving(true);
    dispatch(updateRequestName(batch.id, name));
    setIsSaving(false);
    setEditable(false);
  };

  const openPopup = () => {
    setPopupOpen(true);
  };
  const closePopup = () => {
    setPopupOpen(false);
  };

  const closePopupWithDelay = () => {
    setTimeout(() => {
      setPopupOpen(false);
    }, 1000);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      saveName();
    }
  };

  const shouldShowButton = batch.isHovered || showButton;

  return (
    <Flex>
      {editable && (
        <TextField
          value={name}
          minimal
          onChange={e => {
            setName(e.target.value);
          }}
          onClick={e => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        />
      )}
      {!editable && linked && (
        <StyledNavLink
          to={`/dashboard/batch/${batch.id}`}
          onClick={event => {
            event.stopPropagation();
          }}
        >
          {batchName}
        </StyledNavLink>
      )}
      {!editable && !linked && batchName}
      {!shareInfo.sharedWithMe && (
        <SideContainer>
          {editable && isSaving && <Spinner size={16} />}
          {editable && !isSaving && (
            <Button
              icon="save"
              weight="minimal"
              size="small"
              onClick={e => {
                e.stopPropagation();
                saveName();
              }}
            />
          )}
          {!editable && shouldShowButton && (
            <Button
              icon="edit"
              weight="minimal"
              size="small"
              onClick={e => {
                e.stopPropagation();
                setEditable(!editable);
              }}
            />
          )}
        </SideContainer>
      )}
      {copyable && shouldShowButton && (
        <Popover2
          usePortal
          isOpen={isPopupOpen}
          onClose={closePopup}
          hoverCloseDelay={300}
          position={Position.BOTTOM}
          content={
            <CopyButtonsContainer onClick={closePopupWithDelay}>
              <StyledCopyButton copy={batchName} text="Name" />
              <Divider />
              <StyledCopyButton copy={id} text="ID" />
            </CopyButtonsContainer>
          }
        >
          <SimplifiedTooltip content="Copy name or ID to clipboard">
            <Button size="small" weight="minimal" icon="content_copy" rightIcon="expand_more" onClick={openPopup} />
          </SimplifiedTooltip>
        </Popover2>
      )}
    </Flex>
  );
}
